import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { getItems, ORDER_UPDATED_EVENT } from '@theme/configurator-order';

/**
 * A custom element that displays a cart icon with a bubble showing the
 * current Add to Order item count. Sourced from configurator-order.js's
 * localStorage list, not Shopify's native cart — see that file's header
 * comment for why (native cart can't hold correctly-priced configured
 * items, so it's never the real count once an item is added).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener(ORDER_UPDATED_EVENT, this.onOrderUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.renderCartBubble(this.currentOrderCount, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(ORDER_UPDATED_EVENT, this.onOrderUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** @type {number} */
  get currentOrderCount() {
    return getItems().reduce((sum, item) => sum + (item.quantity || 1), 0);
  }

  /**
   * Handles the page show event when the page is restored from cache (bfcache),
   * where this component's DOM — and its bubble count — is stale.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.renderCartBubble(this.currentOrderCount, false);
    }
  };

  onOrderUpdate = () => {
    this.renderCartBubble(this.currentOrderCount);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the order.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.refs.cartBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
